import {DI_TOKENS} from '@/di/di.tokens';

import {injected} from 'brandi';

import {AbstractTracking, createTracking} from '@specials/web-tracking';
import {EventData, EventName} from '@specials/web-tracking/lib/types';

import {PlatformService} from '@/services/PlatformService/PlatformService';
import {
  AnalyticsAuthType,
  AnalyticsBackButtonType,
  AnalyticsBonusButtonType,
  AnalyticsBonusSwitchButtonType,
  AnalyticsConditionsType,
  AnalyticsInfoModalButtonType,
  AnalyticsInfoModalType,
  AnalyticsItemBonusType,
  AnalyticsItemStateType,
  AnalyticsNavBarButtonType,
  AnalyticsNavigateMbButtonType,
  AnalyticsNotificationsStateType,
  AnalyticsScreenType,
  AnalyticsScrollToItemButtonType,
  AnalyticsShareButtonType,
} from '@/services/TrackingService/constants';
import {AuthEvent, ClientParams, ExtendedEventName} from '@/services/TrackingService/types';

export class TrackingService {
  private readonly tracking: AbstractTracking = createTracking();
  private clientParams: ClientParams = {
    specScreenType: undefined,
    specDbId: undefined,
  };

  constructor(private readonly platformService: PlatformService) {}

  public updateClientParams = (userId?: string): void => {
    this.clientParams = {specDbId: userId, specScreenType: this.getScreenType()};
  };

  public updateLocation = (): void => {
    this.tracking.updateLocation();
  };

  public subscribeTrackingModule = (callback: VoidFunction): void => {
    const trackingCallback = (): void => {
      // Сохраняем данные аналитики в Cookie. Данная механика описана по ссылке на вики:
      // @See: https://wiki.tcsbank.ru/x/BOZBcQ
      this.tracking.saveTrackingStateToCookie();

      callback();
    };

    this.tracking.subscribeTrackingPageLoad(trackingCallback);
    /** MBG-2261: Случаи сбоя CDN, где лежит скрипт трекера данных аналитики,
     * приводят к проблеме - не отрабатывает callback переданный в subscribeTrackingPageLoad.
     * Поэтому добавляем подписку, для ошибки загрузки скрипта с CDN. */
    this.tracking.subscribeTrackingFailure(trackingCallback);
  };

  /** Трекинг закрытия модалок недоступного айтема, открытия всех конфет и завершения проекта */
  public trackClickOnCloseInfoModal(buttonType: AnalyticsInfoModalButtonType, modalType: AnalyticsInfoModalType): void {
    this.sendExtendedEvent('events.specAction20', {specVar: buttonType, specVar2: modalType});
  }

  /** Трекинг клика по кнопке онбординга */
  public trackClickOnOnboardingButton(): void {
    this.sendExtendedEvent('specAction19');
  }

  /** Трекинг клика по кнопке шера */
  public trackClickOnSharesButton(shareButtonType: AnalyticsShareButtonType): void {
    this.sendExtendedEvent('specAction18', {specVar: shareButtonType});
  }

  /** Трекинг клика по переключателю нотификаций */
  public trackClickOnNotificationsSwitchButton(stateType: AnalyticsNotificationsStateType): void {
    this.sendExtendedEvent('specAction17', {specVar: stateType});
  }

  /** Трекинг клика по кнопкам переключения списков бонусов */
  public trackClickOnBonusSwitchButton(switchButtonType: AnalyticsBonusSwitchButtonType): void {
    this.sendExtendedEvent('specAction16', {specVar: switchButtonType});
  }

  /** Трекинг клика по кнопкам в карточке бонуса */
  public trackClickOnBonusCardButton(bonusButtonType: AnalyticsBonusButtonType, bonusId: string): void {
    this.sendExtendedEvent('specAction15', {specVar: bonusButtonType, specVar2: bonusId});
  }

  /** Трекинг клика по кнопке с благотворительностью */
  public trackClickOnCharityButton(): void {
    this.sendExtendedEvent('specAction14');
  }

  /** Трекинг показа модалки с полученным бонусом или пожеланием */
  public trackClickOnBonusCTA(itemId: string, bonusType: AnalyticsItemBonusType): void {
    this.sendExtendedEvent('specAction13', {specVar: itemId, specVar2: bonusType});
  }

  /** Трекинг показа модалки с полученным бонусом или пожеланием */
  public trackShowItemBonusReward(itemId: string, bonusType: AnalyticsItemBonusType, bonusId?: string): void {
    this.sendExtendedEvent('specAction12', {specVar: itemId, specVar2: bonusType, specVar3: bonusId});
  }

  /** Трекинг клика по кнопке получения бонуса */
  public trackClickOnOpenItemBonusButton(itemId: string, bonusesIds: string[], bonusId: string): void {
    this.sendExtendedEvent('specAction11', {specVar: itemId, specVar2: JSON.stringify(bonusesIds), specVar3: bonusId});
  }

  /** Трекинг клика по айтему */
  public trackClickOnItem(
    itemId: string,
    bonusType: AnalyticsItemBonusType,
    bonusesIds: string[],
    stateType: AnalyticsItemStateType,
  ): void {
    this.sendExtendedEvent('specAction10', {
      specVar: itemId,
      specVar2: bonusType,
      specVar3: JSON.stringify(bonusesIds),
      specVar4: stateType,
    });
  }

  /** Трекинг клика по кнопке с энергией */
  public trackClickOnEnergyButton(): void {
    this.sendExtendedEvent('specAction9');
  }

  /** Трекинг клика по кнопке скролла к блоку с айтемами */
  public trackClickOnScrollToItemsButton(chooseItemButtonType: AnalyticsScrollToItemButtonType): void {
    this.sendExtendedEvent('specAction8', {specVar: chooseItemButtonType});
  }

  /** Трекинг клика по кнопке Назад в навигации */
  public trackClickOnNavBackButton(backButtonType: AnalyticsBackButtonType): void {
    this.sendExtendedEvent('specAction7', {specVar: backButtonType});
  }

  /** Трекинг клика по кнопкам навигации в топ баре */
  public trackClickOnNavBarButton(navBarButtonType: AnalyticsNavBarButtonType): void {
    this.sendExtendedEvent('specAction6', {specVar: navBarButtonType});
  }

  /** Трекинг клика по условиям акции */
  public trackClickOnConditions(conditionsType: AnalyticsConditionsType): void {
    this.sendExtendedEvent('specAction5', {specVar: conditionsType});
  }

  /** Трекинг клика по вопросу в FAQ */
  public trackClickOnFaqQuestion(question: string): void {
    this.sendExtendedEvent('specAction4', {specVar: question});
  }

  /** Трекинг клика по инструкции как найти в мб */
  public trackClickOnHowToFindLink(): void {
    this.sendExtendedEvent('specAction3');
  }

  /** Трекинг клика по переходу в мб из лендинга */
  public trackClickOnNavigateToMb(navigateMbButtonType: AnalyticsNavigateMbButtonType): void {
    this.sendExtendedEvent('specAction2', {specVar: navigateMbButtonType});
  }

  /** Трекинг загрузки страницы */
  public trackLoadPage(): void {
    this.sendExtendedEvent('specAction1');
  }

  /** Трекинг авторизации */
  public trackAuthError(errorCode?: string): void {
    const error: AuthEvent = errorCode ? `${AnalyticsAuthType.Error}${errorCode}` : AnalyticsAuthType.UnknownError;
    this.trackAuth(error);
  }

  public trackAuthUserValid(itemsIds?: string[]): void {
    this.trackAuth(AnalyticsAuthType.UserValid, {specVar2: JSON.stringify(itemsIds)});
  }

  private trackAuth = (event: AuthEvent, eventData?: EventData): void => {
    const extendedEventData = eventData ? {...eventData} : {};
    this.sendExtendedEvent('specAuth', {...extendedEventData, specVar: event});
  };

  private sendExtendedEvent = (eventName: ExtendedEventName, eventData?: EventData): void => {
    this.tracking.sendEvent(eventName as EventName, this.addClientParamsToEventData(eventData));
  };

  private addClientParamsToEventData = (eventData?: EventData): EventData => {
    const extendedEventData = eventData ? {...eventData} : {};
    const {specScreenType, specDbId} = this.clientParams;

    extendedEventData.specScreenType = specScreenType;

    if (specDbId) {
      extendedEventData.specDbId = specDbId;
    }

    return extendedEventData;
  };

  private getScreenType(): AnalyticsScreenType {
    return this.platformService.isMb() ? AnalyticsScreenType.Native : AnalyticsScreenType.Web;
  }
}

injected(TrackingService, DI_TOKENS.platformService);
